import React, {useCallback, useMemo} from 'react';
import {Stack, Typography} from '@mui/material';
import SegmentItem from '../segmentItem';
import FolderRow from './FolderRow';
import {countItemsByFolder, getFoldersAtLevel, getItemsAtLevel} from './utils/sourcePackageHelpers';

const EmptyState = () => (
    <Stack alignItems="center" justifyContent="center" sx={{p: 2}}>
        <Typography variant="body1" color="text.secondary" align="center">
            Any documents and highlights you tag will show up here. You can also add your data.
        </Typography>
    </Stack>
);


/**
 * FolderItemsList - Displays folders and items at the current level.
 * Folders are shown first, then items.
 * Supports infinite scroll pagination.
 */
const FolderItemsList = ({
                             currentParentId = null,
                             folders,
                             items,
                             onOpenFolder,
                             addItem,
                             userId,
                             renameFolder,
                             removeFolder,
                             moveFolder,
                             moveItem,
                             removeItem,
                         }) => {
    // Compute filtered lists
    const levelFolders = useMemo(
        () => getFoldersAtLevel(folders, currentParentId),
        [folders, currentParentId]
    );

    const levelItems = useMemo(
        () => getItemsAtLevel(items, currentParentId),
        [items, currentParentId]
    );

    const folderItemCounts = useMemo(
        () => countItemsByFolder(items),
        [items]
    );

    const handleDropSnippet = useCallback((folderId, data) => {
        addItem({type: 'DOCUMENT_FRAGMENT', parentFolderId: folderId || null, userId, ...data});
    }, [addItem, userId]);

    const isEmpty = levelFolders.length === 0 && levelItems.length === 0;

    if (isEmpty) {
        return <EmptyState/>;
    }


    return (
        <Stack spacing={2} flex={1}>
            {levelFolders.map((folder) => (
                <FolderRow
                    key={`folder:${folder.id}`}
                    folderId={folder.id}
                    name={folder.name || 'Untitled'}
                    count={folderItemCounts.get(folder.id) || 0}
                    onOpen={onOpenFolder}
                    onDropSnippet={handleDropSnippet}
                    folders={folders}
                    renameFolder={renameFolder}
                    removeFolder={removeFolder}
                    moveFolder={moveFolder}
                    moveItem={moveItem}
                />
            ))}

            {levelItems.map((item) => (
                <SegmentItem
                    key={`item:${item.id}`}
                    props={{
                        ...item,
                        segmentType: 'saved',
                        query: '',
                        itemId: item.id,
                        folders,
                        moveItem,
                        removeItem,
                    }}
                />
            ))}
        </Stack>
    );
};

export default FolderItemsList;
